import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Copy, XCircle } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { useToastStore } from '@/stores/toast-store';
import { useTapHandler } from '@/hooks/useTapHandler';
import { isDocumentDriver } from '@/lib/driver-kind';
import { MobileActionSheet } from '../MobileActionSheet';
import type { ConnectionConfig, QueryColumn, SchemaMetadata, ColumnMeta } from '@/types';

interface RecordDetailScreenProps {
  params: Record<string, unknown>;
}

export function RecordDetailScreen({ params }: RecordDetailScreenProps) {
  const connectionId = params.connectionId as string;
  const schemaName = params.schema as string;
  const tableName = params.table as string;
  const rowData = params.rowData as Record<string, unknown>;
  const columns = params.columns as QueryColumn[];
  const popScreen = useMobileStore((s) => s.popScreen);
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();

  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [actionSheet, setActionSheet] = useState<{
    items: { label: string; icon?: typeof Copy; onClick: () => void; danger?: boolean }[];
  } | null>(null);

  // Driver kind decides whether save/delete generate SQL or mongosh.
  // The screen is also opened from the desktop's row-tap pathway, so
  // we resolve from the connections query the same way.
  const { data: connections = [] } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
    staleTime: 60_000,
  });
  const driverType = connections.find((c) => c.id === connectionId)?.driverType ?? 'postgresql';
  const isDocDriver = isDocumentDriver(driverType);

  // Primary key has to come from schema metadata: the `columns` prop is the
  // executeQuery result, whose QueryColumn never carries isPrimaryKey. Reading
  // it from there meant pkColumns was empty for *every* table, so save/delete
  // always fell through to a WHERE built from all columns. Mobile only ever
  // browses the connection's default database, so no `?database=` is needed —
  // and this key matches SchemaBrowserScreen's, so the cache is shared.
  const { data: schemaData, isPending: schemaPending } = useQuery<SchemaMetadata>({
    queryKey: ['schema', connectionId],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${connectionId}`),
    staleTime: 60_000,
  });

  const pkColumns = useMemo<string[]>(() => {
    for (const db of schemaData?.databases ?? []) {
      for (const sc of db.schemas) {
        if (sc.name !== schemaName) continue;
        const t = sc.tables.find((tbl) => tbl.name === tableName);
        if (t) {
          return t.columns
            .filter((c: ColumnMeta) => c.isPrimaryKey)
            .map((c: ColumnMeta) => c.name);
        }
      }
    }
    return [];
  }, [schemaData, schemaName, tableName]);

  /** Writes address a row by primary key. Without one there is no WHERE that
   *  provably matches a single row, so editing is disabled and the user is
   *  sent to the query editor. Fails closed while schema is loading. Mongo is
   *  exempt — identity is `_id`, guarded in buildMongoUpdate/buildMongoDelete. */
  const hasRowIdentity = isDocDriver || pkColumns.length > 0;
  const showNoIdentityNotice = !hasRowIdentity && !schemaPending;

  const hasChanges = Object.keys(editedValues).length > 0;

  const getCurrentValue = useCallback(
    (field: string): unknown => {
      if (field in editedValues) return editedValues[field];
      return rowData[field];
    },
    [editedValues, rowData],
  );

  const setFieldValue = useCallback((field: string, value: unknown) => {
    if (!hasRowIdentity) {
      addToast('warning', 'Read-only: no primary key on this table — use the query editor');
      return;
    }
    setEditedValues((prev) => {
      // If reset to original value, remove from edited
      if (value === rowData[field]) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, [rowData, hasRowIdentity, addToast]);

  /** WHERE targeting exactly this record, or null when the table has no
   *  primary key. The previous fallback AND-ed every column together, which
   *  is not an identity — duplicate rows were updated or deleted as a set. */
  const buildWhereClause = useCallback((): string | null => {
    if (pkColumns.length === 0) return null;
    return pkColumns
      .map((col) => {
        const val = rowData[col];
        if (val === null || val === undefined) return `${col} IS NULL`;
        return `${col} = ${quoteValue(val)}`;
      })
      .join(' AND ');
  }, [pkColumns, rowData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!hasChanges) return;
      let sql: string | null;
      if (isDocDriver) {
        sql = buildMongoUpdate(tableName, rowData, editedValues);
      } else {
        const where = buildWhereClause();
        if (where === null) {
          throw new Error('This table has no primary key — use the query editor to change data.');
        }
        sql = `UPDATE ${schemaName}.${tableName} SET ${
          Object.entries(editedValues)
            .map(([field, value]) =>
              value === null ? `${field} = NULL` : `${field} = ${quoteValue(value)}`,
            )
            .join(', ')
        } WHERE ${where}`;
      }
      if (!sql) {
        throw new Error('Cannot build update — no _id on this document.');
      }
      return apiClient.post('/api/query/execute', { connectionId, sql });
    },
    onSuccess: () => {
      addToast('success', 'Record updated');
      setEditedValues({});
      queryClient.invalidateQueries({
        queryKey: ['table-data', connectionId, schemaName, tableName],
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Update failed';
      addToast('error', message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      let sql: string | null;
      if (isDocDriver) {
        sql = buildMongoDelete(tableName, rowData);
      } else {
        const where = buildWhereClause();
        if (where === null) {
          throw new Error('This table has no primary key — use the query editor to delete rows.');
        }
        sql = `DELETE FROM ${schemaName}.${tableName} WHERE ${where}`;
      }
      if (!sql) throw new Error('Cannot build delete — no _id on this document.');
      return apiClient.post('/api/query/execute', { connectionId, sql });
    },
    onSuccess: () => {
      addToast('success', 'Record deleted');
      queryClient.invalidateQueries({
        queryKey: ['table-data', connectionId, schemaName, tableName],
      });
      popScreen();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Delete failed';
      addToast('error', message);
    },
  });

  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const handleDelete = useCallback(() => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    deleteMutation.mutate();
  }, [deleteConfirm, deleteMutation]);

  const handleFieldLongPress = useCallback(
    (field: string) => {
      const currentVal = getCurrentValue(field);
      setActionSheet({
        items: [
          {
            label: 'Set NULL',
            icon: XCircle,
            onClick: () => setFieldValue(field, null),
          },
          {
            label: 'Copy Value',
            icon: Copy,
            onClick: () => {
              const text = currentVal === null || currentVal === undefined
                ? 'NULL'
                : String(currentVal);
              navigator.clipboard.writeText(text).catch(() => {
                addToast('error', 'Copy failed');
              });
              addToast('info', 'Copied to clipboard');
            },
          },
        ],
      });
    },
    [getCurrentValue, setFieldValue, addToast],
  );

  return (
    <div className="h-full flex flex-col">
      {/* Read-only notice — table has no primary key */}
      {showNoIdentityNotice && (
        <div
          className="px-4 py-2 shrink-0 border-b text-[11px] leading-relaxed text-accent-orange"
          style={{ backgroundColor: 'var(--dbgo-panel)', borderColor: 'var(--border-default)' }}
        >
          <span className="font-medium">Read-only — this table has no primary key.</span>{' '}
          DBGo identifies a record by its primary key; without one it cannot target a single
          row, so editing is off. Use the query editor and write an explicit WHERE.
        </div>
      )}

      {/* Save bar */}
      {hasChanges && (
        <div
          className="flex items-center justify-between px-4 shrink-0 border-b"
          style={{
            height: 44,
            backgroundColor: 'var(--dbgo-panel)',
            borderColor: 'var(--border-default)',
          }}
        >
          <span className="text-xs text-accent-yellow">Unsaved changes</span>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="px-4 py-1.5 rounded-md text-sm font-medium text-white active:opacity-70 disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-blue)' }}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {/* Fields. Top-level fields use the editable FieldRow so users
          can change primitive values. Nested objects / arrays render
          as collapsible tree nodes (read-only) — editing those needs
          path-based filters the grid doesn't model, so we punt to the
          query editor for that case. */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {columns.map((col) => {
          const isPK = col.isPrimaryKey === true;
          const value = getCurrentValue(col.name);
          const isNull = value === null || value === undefined;
          const isEdited = col.name in editedValues;
          const isCurrentlyEditing = editingField === col.name;
          const isNested = !isNull && typeof value === 'object';

          if (isNested) {
            // Nested values get the tree renderer instead of an input.
            return (
              <NestedFieldNode
                key={col.name}
                label={col.name}
                type={col.type}
                value={value}
              />
            );
          }

          return (
            <FieldRow
              key={col.name}
              label={col.name}
              type={col.type}
              value={value}
              isNull={isNull}
              isPK={isPK}
              isEdited={isEdited}
              isEditing={isCurrentlyEditing}
              onTap={() => {
                if (!isPK) setEditingField(col.name);
              }}
              onBlur={() => setEditingField(null)}
              onChange={(newVal) => setFieldValue(col.name, newVal)}
              onLongPress={() => handleFieldLongPress(col.name)}
            />
          );
        })}
      </div>

      {/* Bottom actions */}
      <div
        className="flex items-center gap-3 px-4 shrink-0 border-t"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          minHeight: 56,
          backgroundColor: 'var(--dbgo-panel)',
          borderColor: 'var(--border-default)',
        }}
      >
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending || !hasRowIdentity}
          className="px-4 py-2 rounded-md text-sm font-medium active:opacity-70 disabled:opacity-50"
          style={{
            color: 'var(--accent-red)',
            backgroundColor: deleteConfirm ? 'var(--accent-red)' : undefined,
            ...(deleteConfirm ? { color: 'white' } : {}),
          }}
        >
          {deleteConfirm ? 'Confirm Delete' : 'Delete'}
        </button>
        <div className="flex-1" />
        {hasChanges && (
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="px-4 py-2 rounded-md text-sm font-medium text-white active:opacity-70 disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-blue)' }}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      {/* Action sheet */}
      {actionSheet && (
        <MobileActionSheet
          items={actionSheet.items}
          onClose={() => setActionSheet(null)}
        />
      )}
    </div>
  );
}

// ─── Field Row ──────────────────────────────────────

interface FieldRowProps {
  label: string;
  type: string;
  value: unknown;
  isNull: boolean;
  isPK: boolean;
  isEdited: boolean;
  isEditing: boolean;
  onTap: () => void;
  onBlur: () => void;
  onChange: (value: unknown) => void;
  onLongPress: () => void;
}

function FieldRow({
  label,
  type,
  value,
  isNull,
  isPK,
  isEdited,
  isEditing,
  onTap,
  onBlur,
  onChange,
  onLongPress,
}: FieldRowProps) {
  const tapHandlers = useTapHandler({
    onTap: isPK ? undefined : onTap,
    onLongPress,
  });

  const isBool = type.toLowerCase().includes('bool');
  const displayValue = isNull ? '' : String(value ?? '');

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-medium text-text-muted">{label}</span>
        {isPK && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{ backgroundColor: 'var(--accent-yellow)', color: 'var(--dbgo-root)' }}
          >
            PK
          </span>
        )}
        <span className="text-[10px] text-text-muted">{type}</span>
        {isEdited && (
          <span className="text-[10px] text-accent-yellow font-medium ml-auto">modified</span>
        )}
      </div>

      {isBool && !isPK ? (
        <button
          onClick={() => {
            const boolVal = value === true || value === 'true' || value === 't' || value === 1;
            onChange(!boolVal);
          }}
          className="w-full rounded-lg px-3 text-left text-sm border active:opacity-70"
          style={{
            height: 44,
            backgroundColor: isPK ? 'var(--dbgo-root)' : 'var(--dbgo-input)',
            borderColor: isEdited ? 'var(--accent-yellow)' : 'var(--border-default)',
            color: 'var(--text-primary)',
          }}
        >
          {isNull ? (
            <span className="italic text-text-muted">NULL</span>
          ) : (
            String(value === true || value === 'true' || value === 't' || value === 1 ? 'true' : 'false')
          )}
        </button>
      ) : isEditing && !isPK ? (
        <input
          type={isNumericType(type) ? 'number' : 'text'}
          defaultValue={displayValue}
          autoFocus
          onBlur={(e) => {
            const raw = e.target.value;
            if (raw === '' && !isNull) {
              onChange(null);
            } else if (isNumericType(type)) {
              const num = Number(raw);
              if (!isNaN(num)) onChange(num);
            } else {
              onChange(raw);
            }
            onBlur();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          className="w-full rounded-lg px-3 text-sm outline-none border"
          style={{
            height: 44,
            backgroundColor: 'var(--dbgo-input)',
            borderColor: 'var(--accent-blue)',
            color: 'var(--text-primary)',
          }}
        />
      ) : (
        <div
          className="w-full rounded-lg px-3 flex items-center text-sm border cursor-pointer"
          style={{
            height: 44,
            backgroundColor: isPK ? 'var(--dbgo-root)' : 'var(--dbgo-input)',
            borderColor: isEdited ? 'var(--accent-yellow)' : 'var(--border-default)',
            color: 'var(--text-primary)',
          }}
          {...tapHandlers}
        >
          {isNull ? (
            <span className="italic text-text-muted">NULL</span>
          ) : (
            <span className="truncate">{displayValue}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────

function quoteValue(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  // Escape single quotes
  const str = String(value).replace(/'/g, "''");
  return `'${str}'`;
}

function isNumericType(type: string): boolean {
  const lower = type.toLowerCase();
  return (
    lower.includes('int') ||
    lower.includes('numeric') ||
    lower.includes('decimal') ||
    lower.includes('float') ||
    lower.includes('double') ||
    lower.includes('real') ||
    lower.includes('serial')
  );
}

// ─── Nested-value tree ───────────────────────────────
//
// Renders object / array values inline with tap-to-expand. Recurses
// for nested structures. Read-only — editing a value inside a nested
// shape needs path-based filters (e.g. `{"a.b.c": newVal}`) and a
// type-aware editor for arrays, neither of which is in v1. The user
// can always switch to the query editor for that case.

/** Top-level wrapper that renders the field label + the tappable
 *  expandable summary. Mirrors `FieldRow`'s outer layout so the screen
 *  feels uniform. Exported so QueryRecordDetailScreen reuses it. */
export function NestedFieldNode({ label, type, value }: { label: string; type: string; value: unknown }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-medium text-text-muted">{label}</span>
        <span className="text-[10px] text-text-muted">{type || (Array.isArray(value) ? 'array' : 'object')}</span>
      </div>
      <NestedNode value={value} startExpanded />
    </div>
  );
}

/** Recursive renderer for any value. Primitives render inline,
 *  objects/arrays render as a tappable summary that toggles a nested
 *  list. Pass `depth` to indent nested levels — first level renders
 *  flush; each step adds a small left padding. */
function NestedNode({ value, depth = 0, startExpanded = false }: {
  value: unknown;
  depth?: number;
  startExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(startExpanded);

  if (value === null || value === undefined) {
    return <PrimitiveLine display="NULL" muted />;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <PrimitiveLine display="[empty array]" muted />;
    return (
      <Collapsible
        label={`[${value.length} item${value.length === 1 ? '' : 's'}]`}
        accent="blue"
        depth={depth}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
      >
        {value.map((item, i) => (
          <div key={i} className="space-y-1">
            <div className="text-[10px] text-text-muted px-2">[{i}]</div>
            <NestedNode value={item} depth={depth + 1} />
          </div>
        ))}
      </Collapsible>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <PrimitiveLine display="{empty object}" muted />;
    return (
      <Collapsible
        label={`{${entries.length} field${entries.length === 1 ? '' : 's'}}`}
        accent="purple"
        depth={depth}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
      >
        {entries.map(([key, v]) => (
          <div key={key} className="space-y-1">
            <div className="text-[10px] text-text-muted px-2 font-mono">{key}</div>
            <NestedNode value={v} depth={depth + 1} />
          </div>
        ))}
      </Collapsible>
    );
  }

  // Primitive — string / number / boolean. Strings get a different
  // tint than numbers / booleans to match the desktop grid colors.
  return <PrimitiveLine display={String(value)} />;
}

function PrimitiveLine({ display, muted = false }: { display: string; muted?: boolean }) {
  return (
    <div
      className="w-full rounded-lg px-3 flex items-center text-sm border"
      style={{
        minHeight: 36,
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: 'var(--dbgo-input)',
        borderColor: 'var(--border-default)',
        color: muted ? 'var(--text-muted)' : 'var(--text-primary)',
        fontStyle: muted ? 'italic' : 'normal',
      }}
    >
      <span className="truncate">{display}</span>
    </div>
  );
}

function Collapsible({
  label,
  accent,
  depth,
  expanded,
  onToggle,
  children,
}: {
  label: string;
  accent: 'blue' | 'purple';
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const accentColor = accent === 'blue' ? 'var(--accent-blue)' : 'var(--accent-purple)';
  return (
    <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 rounded-lg px-3 text-left text-sm border active:opacity-70"
        style={{
          minHeight: 36,
          paddingTop: 6,
          paddingBottom: 6,
          backgroundColor: 'var(--dbgo-input)',
          borderColor: 'var(--border-default)',
          color: accentColor,
        }}
      >
        <ChevronRight
          size={14}
          style={{ transition: 'transform 120ms ease', transform: expanded ? 'rotate(90deg)' : 'none' }}
          className="text-text-muted flex-shrink-0"
        />
        <span className="font-mono text-xs">{label}</span>
      </button>
      {expanded && (
        <div
          className="mt-1 space-y-2"
          style={{
            marginLeft: 12,
            borderLeft: `1px solid var(--border-default)`,
            paddingLeft: 10,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Mongo write helpers ────────────────────────────
//
// Mirror the desktop's `mongoLiteral` + `generateChangeMongosh`. The
// mobile screen edits one document at a time, so build a single
// `updateOne` / `deleteOne` and post it.

function mongoLiteral(value: unknown, fieldName: string): string {
  if (value === null || value === undefined) return 'null';
  if (value instanceof Date) return `ISODate(${JSON.stringify(value.toISOString())})`;
  if (typeof value === 'string') {
    if (fieldName === '_id' && /^[0-9a-fA-F]{24}$/.test(value)) {
      return `ObjectId(${JSON.stringify(value)})`;
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function buildMongoUpdate(
  collection: string,
  rowData: Record<string, unknown>,
  edited: Record<string, unknown>,
): string | null {
  const id = rowData['_id'];
  if (id === undefined || id === null) return null;
  const setFields = Object.entries(edited)
    .filter(([k]) => k !== '_id' && !k.startsWith('__dbgo'))
    .map(([k, v]) => `${JSON.stringify(k)}: ${mongoLiteral(v, k)}`);
  if (setFields.length === 0) return null;
  return `db.${collection}.updateOne({_id: ${mongoLiteral(id, '_id')}}, {$set: {${setFields.join(', ')}}})`;
}

function buildMongoDelete(collection: string, rowData: Record<string, unknown>): string | null {
  const id = rowData['_id'];
  if (id === undefined || id === null) return null;
  return `db.${collection}.deleteOne({_id: ${mongoLiteral(id, '_id')}})`;
}
